import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import jwt_decode from 'jwt-decode';
import { User } from '../../models/user';
import { environment } from '../../../environments/environment';
import { Role } from '../../models/role';
import { ZoneService } from '../zone.service';
import { ISeries } from '../../models/series';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
	private router = inject(Router);
	private zoneService = inject(ZoneService);
	private http = inject(HttpClient);

	private userSubject: BehaviorSubject<User>;
	public user: Observable<User>;

	constructor() {
		this.userSubject = new BehaviorSubject<User>(this.decodeUser(JSON.parse(localStorage.getItem('token'))));
		this.user = this.userSubject.asObservable();
	}

	public get userValue(): User {
		return this.userSubject.value;
	}

	public set userValue(value: User) {
		this.userSubject.next(value);
	}

	login(username: string, password: string) {
		return this.http.post<any>(`${environment.apiUrl}/auth/signin`, { username, password }, { observe: 'response' }).pipe(
			tap((data) => {
				const authorization = data.headers.get('Authorization').replace('Bearer ', '');
				const refresh = data.headers.get('Refresh-Token');
				// store user details and jwt token in local storage to keep user logged in between page refreshes
				localStorage.setItem('token', JSON.stringify(authorization));
				localStorage.setItem('unsuccessfulAttempts', '0');
				localStorage.setItem('refresh_token', refresh);
				const user = this.decodeUser(authorization);
				this.userSubject.next(user);
			})
		);
	}

	refresh(status?) {
		let refreshToken = localStorage.getItem('refresh_token');
		return this.http
			.post<any>(`${environment.apiUrl}/auth/refreshtoken`, { refreshToken: refreshToken }, { observe: 'response' })
			.pipe(
				map((data) => {
					const authorization = data.headers.get('Authorization').replace('Bearer ', '');
					localStorage.setItem('token', JSON.stringify(authorization));
					const user = this.decodeUser(authorization);
					this.userSubject.next(user);
					return true;
				})
			)
			.toPromise()
			.catch(() => {
				this.logout(status);
				return false;
			});
	}

	private decodeUser(authorizationToken: string): User {
		if (!authorizationToken) {
			return null;
		}
		const decodedJwt = jwt_decode(authorizationToken);
		let token = authorizationToken;
		let role = Role[decodedJwt.roles[0]];
		let username = decodedJwt.sub;
		// expiration time is stored in seconds. we have to multiply by 1000 to convert it to ms.
		let expiresAt = decodedJwt.exp * 1000;
		let name = decodedJwt.fullName;
		let badgeId = decodedJwt.badgeId;
		let id = decodedJwt.id;
		let technology = decodedJwt.technology;
		let townId = decodedJwt.townId;
		let printerType = decodedJwt.printerType;
		let series: ISeries = {
			id: decodedJwt.seriesId,
			seriesNumber: decodedJwt.seriesNumber,
			seriesPurpose: decodedJwt.seriesPurpose
		};

		const user = new User();
		user.setUserFields({ username, name, role, expiresAt, id, token, badgeId, technology, townId, printerType, series });

		return user;
	}

	logout(status?) {
		localStorage.clear();
		this.zoneService.clearZoneNumber();
		this.userSubject.next(null);
		this.router.navigate(['/login'], { queryParams: { status } });
	}

	isOfficer(): boolean {
		return this.userValue.role === Role.LEO || this.userValue.role === Role.PEA;
	}
}
